/**
 * Immutable Repository Proxy Constructor
 *
 * Creates type-safe, immutable chainable proxies for RPC repositories.
 * Each chain method returns a new proxy instance, preventing race conditions.
 * Supports await syntax: await repo.where('active', true).limit(10)
 *
 * Base proxy (пустая цепочка) кэшируется по serviceName — ссылка стабильна
 * между рендерами React. Цепочные методы (.where(), .limit() и т.д.) всегда
 * возвращают новый Proxy, поэтому кэширование безопасно.
 */

import { fetcher } from '@platform/client/helpers/fetcher';
import type { AwaitableRpcClient, RowOf } from './types';

export interface RepositoryProxyOptions {
  terminalMethods: string[];
}

/** Кэш base proxy по serviceName — стабильная ссылка для React-компонентов */
const proxyCache = new Map<string, AwaitableRpcClient<object>>();

/**
 * Создаёт типобезопасный прокси для репозитория с поддержкой цепочек и await.
 * - Цепочные методы возвращают новый прокси (иммутабельность цепочки).
 * - then-трап делает прокси "ожидаемым" и резолвит в RowOf<T>[] по умолчанию.
 * - Терминальные методы должны исполняться немедленно и возвращать строго типизированные Promise.
 * - Base proxy кэшируется: useXxxRepository() возвращает одну и ту же ссылку.
 */
export function makeRepositoryProxy<T extends object = object>(
  serviceName: string,
  options: RepositoryProxyOptions
): AwaitableRpcClient<T> {
  const cached = proxyCache.get(serviceName);
  if (cached) return cached as AwaitableRpcClient<T>;

  const base: Record<string, unknown> = {};
  const createProxyFrom = (chain: { method: string, args: unknown[] }[]): AwaitableRpcClient<T> => new Proxy(base, {
    get: (_target, prop): unknown => {
      // Promise-style await: execute current chain when awaited
      if (prop === 'then') {
        return (onFulfilled: (value: RowOf<T>[]) => unknown, onRejected?: (reason: unknown) => unknown): Promise<unknown> => {
          const data = { fn: serviceName, args: chain };
          const p = fetcher.post('/rpc', data).then(res => res?.data as unknown as RowOf<T>[]);
          return p.then(onFulfilled, onRejected);
        };
      }

      const methodName = String(prop);

      // Client-side only: returns the registration key without an RPC call
      if (methodName === 'getRepositoryCode') {
        return () => serviceName;
      }

      // Terminal methods execute immediately and return a Promise
      if (options.terminalMethods.includes(methodName)) {
        return (...args: unknown[]) => {
          const next = [...chain, { method: methodName, args }];
          const data = { fn: serviceName, args: next };
          return fetcher.post('/rpc', data).then(res => res?.data as unknown);
        };
      }

      // Chainable methods return a NEW proxy (immutable chain)
      return (...args: unknown[]): unknown => {
        const next = [...chain, { method: methodName, args }];
        return createProxyFrom(next);
      };
    }
  }) as unknown as AwaitableRpcClient<T>;

  const proxy = createProxyFrom([]);
  proxyCache.set(serviceName, proxy as AwaitableRpcClient<object>);
  return proxy;
}
